/** I tredici canali di Android. Gli stessi id che `Canali.kt` conosce. */
export const CANALI = {
    riunioni: 'riunioni',
    firme: 'firme',
    turni: 'turni',
    divise: 'divise',
    spese: 'spese',
    viaggi: 'viaggi',
    viaggiUrgente: 'viaggi-urgente',
    mensa: 'mensa',
    mensaUrgente: 'mensa-urgente',
    chat: 'chat',
    buste: 'buste',
    raccolte: 'raccolte',
    sistema: 'sistema',
};
/**
 * La tavola, specie per specie. **Tutte e cinquantasei**: una specie che manca qui è una
 * specie che si presenta a caso, e il cancello `check:presentazione` pretende che non ce ne
 * siano.
 *
 * L'ordine è quello di `ParametriNotifica`, così le due si leggono affiancate.
 */
const TAVOLA = {
    // ── riunioni ──────────────────────────────────────────────────────────────────────────
    'riunione.invito': {
        canale: CANALI.riunioni,
        livello: 'attiva',
        risponde: true,
        azioni: 'invito',
    },
    // Prende i tre tasti dell'invito: la domanda è la stessa domanda chiusa («ci sei al nuovo
    // orario?») e la notifica mostra tutto quel che serve a rispondere.
    'riunione.spostata': {
        canale: CANALI.riunioni,
        livello: 'attiva',
        risponde: true,
        azioni: 'invito',
    },
    // Lo stesso spostamento detto al **titolare**, quando l'ha fatto un altro: attivo, e senza
    // tasti ne' `risponde` — non c'e' niente da riconfermare a casa propria
    // (avvisoAlTitolareSenzaTasti).
    'riunione.spostataTitolare': { canale: CANALI.riunioni, livello: 'attiva' },
    /*
      La sala cambiata a ora invariata: **attiva come lo spostamento**, perche' chi non la legge
      va nella stanza sbagliata — ma **senza `risponde`**, perche' non c'e' niente da
      riconfermare: le risposte restano quelle che erano.
    */
    'riunione.cambiataSala': { canale: CANALI.riunioni, livello: 'attiva' },
    'riunione.annullata': { canale: CANALI.riunioni, livello: 'attiva' },
    // Fra dieci minuti. È l'esempio di manuale di «fra un'ora non conta più».
    'riunione.fraPoco': { canale: CANALI.riunioni, livello: 'urgente' },
    // L'organizzatore di una riunione da dodici invitati riceve dodici risposte in un
    // pomeriggio: nessuna vale una vibrazione. Si trovano nella pila della riunione.
    'riunione.risposta': { canale: CANALI.riunioni, livello: 'passiva' },
    'riunione.tolto': { canale: CANALI.riunioni, livello: 'attiva' },
    // La videochiamata: canale delle riunioni, perche' e' una riunione vista da un altro lato.
    // Attiva e senza tasti: non c'e' niente a cui rispondere, c'e' un posto dove entrare.
    'videochiamata.invito': { canale: CANALI.riunioni, livello: 'attiva' },
    // Qualcuno aspetta davanti alla porta adesso: fra dieci minuti se ne e' andato.
    'videochiamata.bussa': { canale: CANALI.riunioni, livello: 'urgente' },
    // La persona stessa la sta aspettando sul telefono, adesso: deve suonare subito.
    'videochiamata.continua': { canale: CANALI.riunioni, livello: 'urgente' },
    // Lo squillo: un collega e' in linea adesso, e fra un minuto non lo e' piu'. Sul canale della
    // chat, che e' alto su tutte le versioni dell'app: un'app che non conosce lo squillo lo mostra
    // almeno come un messaggio che suona. Quella che lo conosce lo disegna da se' (`squillo` nei dati).
    'videochiamata.squillo': { canale: CANALI.chat, livello: 'urgente' },
    // La persa: non c'e' piu' niente a cui rispondere adesso, c'e' qualcuno da richiamare.
    'videochiamata.persa': { canale: CANALI.riunioni, livello: 'attiva' },
    // ── firme, richieste, proposte ────────────────────────────────────────────────────────
    // Attive e non urgenti: fra un'ora una firma è uguale a adesso. È il criterio, non la
    // gravità — una firma ferma da tre giorni è importante e non è urgente.
    'firma.attesa': { canale: CANALI.firme, livello: 'attiva' },
    'firma.riserve': { canale: CANALI.firme, livello: 'attiva' },
    'firma.dimenticata': { canale: CANALI.firme, livello: 'attiva' },
    'richiesta.decisa': { canale: CANALI.firme, livello: 'attiva' },
    // Stesso canale della decisione — è la stessa cosa che si aspettava — ma livello
    // **passivo**: non c'è niente da fare adesso, e svegliare un telefono per dire che un
    // periodo già passato non si farà è rumore. Si trova aprendo la bacheca.
    'richiesta.scaduta': { canale: CANALI.firme, livello: 'passiva' },
    // Per chi doveva firmare: passiva anche lei, per la stessa ragione — non c'e' niente da firmare
    // adesso. Si trova in bacheca, e non sveglia nessuno di notte per una cosa gia' chiusa.
    'richiesta.scadutaPerChiFirma': { canale: CANALI.firme, livello: 'passiva' },
    'richiesta.annullata': { canale: CANALI.firme, livello: 'attiva' },
    'proposta.ricevuta': {
        canale: CANALI.firme,
        livello: 'attiva',
        risponde: true,
        azioni: 'proposta',
    },
    // A chi ha proposto: è l'esito di una cosa sua, e non c'è niente da fare.
    'proposta.accettata': { canale: CANALI.firme, livello: 'passiva' },
    'proposta.rifiutata': { canale: CANALI.firme, livello: 'passiva' },
    'proposta.ritirata': { canale: CANALI.firme, livello: 'attiva' },
    // ── le raccolte ───────────────────────────────────────────────────────────────────────
    // L'unica coppia con l'elenco: cinque righe, una per famiglia. Il livello lo alza chi manda
    // (`urgente` mai; le 08:30 attive, le 18:00 passive — vedi `riepilogo.service.ts`).
    'riepilogo.giornata': {
        canale: CANALI.raccolte,
        livello: 'attiva',
        stile: 'elenco',
        azioni: 'riepilogo',
    },
    // Sabato mattina, e vale da lunedì: un quadro non sveglia nessuno.
    'assenze.settimana': {
        canale: CANALI.raccolte,
        livello: 'passiva',
        stile: 'elenco',
        azioni: 'apri',
    },
    // ── turni ─────────────────────────────────────────────────────────────────────────────
    'turno.cambiato': { canale: CANALI.turni, livello: 'attiva' },
    'turno.giorniCambiati': { canale: CANALI.turni, livello: 'attiva' },
    'turno.nota': { canale: CANALI.turni, livello: 'attiva' },
    'turno.ritirati': { canale: CANALI.turni, livello: 'attiva' },
    'turni.pubblicati': { canale: CANALI.turni, livello: 'attiva' },
    // ── divise ────────────────────────────────────────────────────────────────────────────
    'divise.daApprovare': { canale: CANALI.divise, livello: 'attiva' },
    // Una buona notizia su cui non c'è niente da fare.
    'divise.approvata': { canale: CANALI.divise, livello: 'passiva' },
    'divise.rifiutata': { canale: CANALI.divise, livello: 'attiva' },
    // tregenereAvvisiAnnullo: annullata dal magazzino, come un rifiuto.
    'divise.annullata': { canale: CANALI.divise, livello: 'attiva' },
    'divise.consegnata': { canale: CANALI.divise, livello: 'attiva' },
    // ── note spese ────────────────────────────────────────────────────────────────────────
    'notaSpese.approvata': { canale: CANALI.spese, livello: 'attiva' },
    'notaSpese.rifiutata': { canale: CANALI.spese, livello: 'attiva' },
    // tregenereAvvisiAnnullo: ritirata dall'ufficio, torna in bozza: c'e' da rifarla.
    'notaSpese.ritirata': { canale: CANALI.spese, livello: 'attiva' },
    // Il rimborso è arrivato: si trova, non chiama.
    'notaSpese.rimborsata': { canale: CANALI.spese, livello: 'passiva' },
    'notaSpese.daRivedere': { canale: CANALI.spese, livello: 'attiva' },
    'notaSpese.sollecito': { canale: CANALI.spese, livello: 'attiva' },
    // ── viaggi, spedizioni, logistica, trasferte ──────────────────────────────────────────
    'viaggio.assegnato': { canale: CANALI.viaggi, livello: 'attiva' },
    'viaggio.promemoria': { canale: CANALI.viaggiUrgente, livello: 'urgente' },
    // Una tappa aggiunta a viaggio partito: chi guida deve saperlo **adesso**.
    'viaggio.tappaAggiunta': { canale: CANALI.viaggiUrgente, livello: 'urgente' },
    // Stesso canale e stessa urgenza della tappa aggiunta: è lo stesso fatto — il percorso di
    // oggi è cambiato mentre l'autista è in strada — visto dall'altro lato.
    'viaggio.spedizioneAggiunta': { canale: CANALI.viaggiUrgente, livello: 'urgente' },
    // `urgente: true` quando parte entro ventiquattr'ore — lo sa chi manda, non questa tavola.
    'viaggio.senzaAutista': { canale: CANALI.viaggi, livello: 'attiva' },
    'viaggio.sollecito': { canale: CANALI.viaggiUrgente, livello: 'urgente' },
    'viaggio.tolto': { canale: CANALI.viaggi, livello: 'attiva' },
    // tregenereAvvisiAnnullo: il giro sparisce dalla giornata di chi guida.
    'viaggio.annullato': { canale: CANALI.viaggi, livello: 'attiva' },
    // `urgente: true` quando la spedizione è marcata urgente.
    'spedizione.assegnata': { canale: CANALI.viaggi, livello: 'attiva' },
    // Passiva: ricorda una cosa che e' gia' tua, non ne chiede una nuova. Un promemoria che
    // suona come un ritiro urgente insegna a non fidarsi dei ritiri urgenti.
    'spedizione.ancoraTua': { canale: CANALI.viaggi, livello: 'passiva' },
    'spedizione.urgente': { canale: CANALI.viaggiUrgente, livello: 'urgente' },
    // Chiusa da sola dopo cinque giorni: è una contabilità, non una notizia.
    'spedizione.autoCompletata': { canale: CANALI.viaggi, livello: 'passiva' },
    'logistica.praticheScadute': { canale: CANALI.viaggi, livello: 'passiva', stile: 'elenco' },
    'trasferta.chiusa': { canale: CANALI.viaggi, livello: 'attiva' },
    // ── buste paga ────────────────────────────────────────────────────────────────────────
    // Il canale è a importanza bassa: una busta paga disponibile non è una cosa da fare, è una
    // cosa che c'è. Non sveglia nessuno, e si trova.
    'bustaPaga.disponibile': { canale: CANALI.buste, livello: 'passiva' },
    'bustaPaga.ritirata': { canale: CANALI.buste, livello: 'passiva' },
    // ── mensa ─────────────────────────────────────────────────────────────────────────────
    'mensa.promemoria': { canale: CANALI.mensa, livello: 'attiva', stile: 'elenco' },
    // Fra dieci minuti l'ordine si chiude: dopo, questa notifica è carta straccia.
    'mensa.daConfermare': { canale: CANALI.mensaUrgente, livello: 'urgente' },
    'mensa.modificheNonConfermate': { canale: CANALI.mensaUrgente, livello: 'urgente' },
    'mensa.piattoTolto': { canale: CANALI.mensa, livello: 'attiva' },
    'mensa.sedeSpenta': { canale: CANALI.mensa, livello: 'attiva' },
    // ── chat ──────────────────────────────────────────────────────────────────────────────
    /*
      Attiva e **non** urgente, anche se è la più viva di tutte: un messaggio di lavoro che buca
      il Non Disturbare alle undici di sera è il modo più rapido per far spegnere la chat a tutti.
      Chi ha bisogno che una persona lo senta a ogni costo ha il telefono.
    */
    /*
      **Rispondere dalla notifica** (N2, deciso da Cosimo il 2026-10-01: «sì, Android *e* iPhone»).
  
      `azioni: 'chat'` apre un gruppo con un tasto solo, e quel tasto apre un **campo di testo**
      invece di agire subito: su Android è un `RemoteInput`, su iPhone un
      `UNTextInputNotificationAction`. Il nome del gruppo è `chat` e non `rispondi` per la stessa
      ragione degli altri — un gruppo raccoglie i tasti di una **famiglia**, e il giorno che se ne
      aggiunge un secondo (per esempio «segna come letto») entra qui dentro invece di inaugurare
      un gruppo nuovo.
  
      **Non c'è nessuna conferma a schermo.** La risposta si mette in fila e parte quando la app
      torna viva; dire «risposto» prima che il server l'abbia presa sarebbe dire una cosa che non è
      ancora vera, ed è la regola che vale su tutt'e due i sistemi.
  
      `data.conversationId` c'è già: la risposta sa dove andare senza che il server mandi niente
      di nuovo.
    */
    'chat.messaggio': { canale: CANALI.chat, livello: 'attiva', risponde: true, azioni: 'chat' },
    'chat.segnalazione': { canale: CANALI.chat, livello: 'attiva' },
    // ── persona e sistema ─────────────────────────────────────────────────────────────────
    'utenza.dismissione': { canale: CANALI.sistema, livello: 'attiva' },
    // Un invito ad aggiornare, non un allarme: si legge quando si apre il telefono.
    'app.nuovaVersione': { canale: CANALI.sistema, livello: 'passiva' },
    'automazioni.guaste': { canale: CANALI.sistema, livello: 'attiva', stile: 'elenco' },
    // La cura non deve gridare quanto l'allarme: sostituisce l'allarme e basta.
    'automazioni.riprese': { canale: CANALI.sistema, livello: 'passiva' },
    'servizi.rialzati': { canale: CANALI.sistema, livello: 'passiva' },
};
// ------------------------------------------------------------------------ le pile e i collassi
/** Il primo valore utile fra quelli, come stringa. Serve a comporre pile e chiavi. */
function primo(dati, chiavi) {
    for (const k of chiavi) {
        const v = dati[k];
        if (v === undefined || v === null || v === '')
            continue;
        return String(v);
    }
    return null;
}
/**
 * La pila di una specie: **l'oggetto**, quando c'è; altrimenti la famiglia.
 *
 * Quando l'oggetto manca — un `data` senza id — si ripiega sulla famiglia invece di non
 * impilare affatto: due avvisi di viaggi diversi insieme sono meno sbagliati di dieci avvisi
 * sciolti che coprono lo schermo.
 */
function pilaDi(specie, dati) {
    if (specie.startsWith('riunione.')) {
        const id = primo(dati, ['bookingId']);
        return id === null ? 'riunioni' : `riunione:${id}`;
    }
    if (specie.startsWith('videochiamata.')) {
        const codice = primo(dati, ['codice']);
        return codice === null ? 'videochiamate' : `videochiamata:${codice}`;
    }
    if (specie.startsWith('firma.'))
        return 'firme';
    if (specie.startsWith('richiesta.'))
        return 'richieste';
    if (specie.startsWith('proposta.'))
        return 'proposte';
    if (specie.startsWith('turno.') || specie.startsWith('turni.')) {
        const rep = primo(dati, ['departmentId']);
        const sett = primo(dati, ['weekStart']);
        return rep === null || sett === null ? 'turni' : `turni:${rep}:${sett}`;
    }
    if (specie.startsWith('divise.')) {
        const id = primo(dati, ['uniformRequestId']);
        return id === null ? 'divise' : `divise:${id}`;
    }
    if (specie.startsWith('notaSpese.')) {
        const id = primo(dati, ['expenseId', 'expenseNoteId']);
        return id === null ? 'spese' : `spese:${id}`;
    }
    if (specie.startsWith('viaggio.')) {
        const id = primo(dati, ['tripId']);
        return id === null ? 'viaggi' : `viaggio:${id}`;
    }
    if (specie.startsWith('spedizione.')) {
        const id = primo(dati, ['transferId']);
        return id === null ? 'spedizioni' : `spedizione:${id}`;
    }
    if (specie.startsWith('logistica.') || specie === 'trasferta.chiusa')
        return 'logistica';
    if (specie.startsWith('chat.')) {
        const id = primo(dati, ['conversationId']);
        return id === null ? 'chat' : `chat:${id}`;
    }
    if (specie.startsWith('mensa.'))
        return 'mensa';
    if (specie.startsWith('bustaPaga.'))
        return 'documenti';
    if (specie === 'riepilogo.giornata' || specie === 'assenze.settimana')
        return 'riepilogo';
    return 'sistema';
}
/**
 * Che cosa questo avviso **sostituisce**, quando la risposta è «un altro avviso sulla stessa
 * cosa che ormai non serve più».
 *
 * Non è il raggruppamento: la pila accumula, questo cancella. Cinque casi e non di più —
 * sostituire è un gesto che *toglie* qualcosa dagli occhi di una persona, e va fatto solo
 * quando il nuovo **contiene** il vecchio.
 */
function collassoDi(specie, dati) {
    // La conferma delle 09:30 sostituisce il promemoria delle 08:30: sono la stessa domanda con
    // un'ora in meno, e due avvisi sulla mensa dello stesso giorno sono uno di troppo.
    if (specie === 'mensa.promemoria' || specie === 'mensa.daConfermare') {
        const g = primo(dati, ['giorno', 'day']);
        return g === null ? 'mensa' : `mensa:${g}`;
    }
    // Il sollecito sostituisce il senza-autista: stesso viaggio, stesso problema, più vicino.
    if (specie === 'viaggio.senzaAutista' || specie === 'viaggio.sollecito') {
        const id = primo(dati, ['tripId']);
        return id === null ? null : `viaggio:${id}:copertura`;
    }
    // Chi non ha aperto la raccolta del mattino non deve trovarne due la sera: la seconda
    // contiene la prima.
    if (specie === 'riepilogo.giornata')
        return 'riepilogo';
    // L'invito vecchio, con «Vengo» sotto, su una riunione spostata è un tasto su un orario che
    // non esiste più.
    if (specie === 'riunione.invito' ||
        specie === 'riunione.spostata' ||
        specie === 'riunione.spostataTitolare' ||
        specie === 'riunione.cambiataSala' ||
        specie === 'riunione.annullata') {
        const id = primo(dati, ['bookingId']);
        return id === null ? null : `riunione:${id}:stato`;
    }
    // La cura sostituisce l'allarme, e non gli si accatasta sopra.
    if (specie === 'automazioni.guaste' || specie === 'automazioni.riprese')
        return 'automazioni';
    /*
      La chiamata persa prende il posto di «X ti sta chiamando» (persaSostituisceLoSquillo, 2026-11-01,
      da-fare §5 tredecies 18, §84.3). Su iPhone lo squillo e' un avviso con la chiave `squillo:<id>`
      (`SquilliService.suona`), e la spinta silenziosa della fine non lo toglie: restava li' a dire che
      qualcuno chiamava ancora. Con la stessa chiave APNs lo sostituisce. `squilloPerso` e non
      `squillo`: con `squillo` il tocco aprirebbe la schermata «ti chiama» invece di «richiama».
    */
    if (specie === 'videochiamata.persa') {
        const id = primo(dati, ['squilloPerso']);
        return id === null ? null : `squillo:${id}`;
    }
    return null;
}
// ------------------------------------------------------------------------------ la funzione
const SOPRA = {
    passiva: 'attiva',
    attiva: 'urgente',
    urgente: 'urgente',
};
const RILEVANZA = {
    urgente: 0.9,
    attiva: 0.6,
    passiva: 0.2,
};
/**
 * Come si presenta questa notifica.
 *
 * `dati` è il `data` che la notifica porta: da lì escono gli identificativi che compongono la
 * pila e la chiave di sostituzione, e il `urgente: true` con cui chi manda alza il livello di
 * un gradino (§ il capoverso in testa: si alza, non si abbassa).
 */
export function presentazione(specie, dati = {}) {
    const riga = TAVOLA[specie];
    const alzata = dati.urgente === true;
    const livello = alzata ? SOPRA[riga.livello] : riga.livello;
    return {
        // Il canale segue l'urgenza dove i due esistono a coppie (viaggi, mensa): un canale solo
        // costringerebbe il promemoria e la scadenza a suonare uguale, e per non sentire il primo
        // si spegnerebbe anche il secondo.
        canale: alzata && riga.canale === CANALI.viaggi
            ? CANALI.viaggiUrgente
            : alzata && riga.canale === CANALI.mensa
                ? CANALI.mensaUrgente
                : riga.canale,
        livello,
        rilevanza: riga.risponde === true ? 0.9 : RILEVANZA[livello],
        stile: riga.stile ?? 'testo',
        pila: pilaDi(specie, dati),
        collasso: collassoDi(specie, dati),
        /*
          I tasti li decide **la riga della specie**, non il `data` della notifica.
    
          Le tre firme di assenza, per esempio, non ne hanno nessuno, ed è una decisione scritta
          (`push/categorie.ts`): approvare un'assenza vuol dire aver guardato la copertura del
          reparto, e un tasto «Approva» sulla schermata di blocco è un modo per far firmare alla
          cieca usando la comodità come esca. Finché quella riga tace, nessun `data` può far
          comparire dei tasti — nemmeno i tre di una riunione.
        */
        azioni: riga.azioni ?? null,
    };
}
/** Le specie che la tavola conosce. La prova pretende che siano tutte. */
export function specieConosciute() {
    return Object.keys(TAVOLA);
}
//# sourceMappingURL=notifiche-presentazione.js.map